import { useState } from 'react';
import { Button, IconButton, InputAdornment, TextField } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import UsersService from '../../common/Rest/Services/UserService/UsersService';
import './LoginPage.css';

interface GoogleAccountLinkPanelProps {
  idToken: string;
  maskedEmail?: string | null;
  onAuthenticated: (token: string) => void;
  onCancel?: () => void;
}

const GoogleAccountLinkPanel: React.FC<GoogleAccountLinkPanelProps> = ({
  idToken,
  maskedEmail,
  onAuthenticated,
  onCancel,
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleConfirmPassword = async () => {
    if (!password) {
      setError('Введіть пароль від акаунта Readerly.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setMessage(null);
      const result = await UsersService.confirmGoogleLinkWithPassword({
        idToken,
        password,
      });
      onAuthenticated(result.token);
    } catch {
      setError('Не вдалося підтвердити акаунт. Перевірте пароль або скористайтесь відновленням.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecoveryRequest = async () => {
    try {
      setSubmitting(true);
      setError(null);
      const result = await UsersService.requestGoogleLinkPasswordRecovery({ idToken });
      setMessage(result.message);
    } catch {
      setError('Не вдалося надіслати лист для відновлення. Спробуйте ще раз.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="google-link-panel">
      <div>
        <h2>Акаунт уже існує</h2>
        <p>
          Ми знайшли акаунт Readerly для {maskedEmail || 'цієї пошти'}. Підтвердіть його,
          щоб прив'язати Google.
        </p>
      </div>

      <TextField
        label="Пароль Readerly"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            handleConfirmPassword();
          }
        }}
        fullWidth
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showPassword ? 'Приховати пароль' : 'Показати пароль'}
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                  tabIndex={-1}
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />

      {error && <div className="error-message">{error}</div>}
      {message && <div className="success-message">{message}</div>}

      <Button variant="contained" type="button" onClick={handleConfirmPassword} disabled={submitting} fullWidth>
        {submitting ? 'Підтверджуємо...' : "Прив'язати Google і увійти"}
      </Button>

      <Button variant="text" type="button" onClick={handleRecoveryRequest} disabled={submitting} fullWidth>
        Я забув пароль
      </Button>

      {onCancel && (
        <Button variant="text" type="button" onClick={onCancel} disabled={submitting} fullWidth>
          Скасувати
        </Button>
      )}
    </div>
  );
};

export default GoogleAccountLinkPanel;
